<template>
  <div
    id="img_container"
    :class="{ dragover: isDraggedOver && canEdit }"
    @dragover.prevent
    @dragenter.prevent="onDragEnter"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div v-if="!isLoading" class="img_ready">
      <div
        v-if="imageUrl"
        class="img_contains"
        :class="{ hasImage: imageUrl !== null }"
      >
        <img
          class="uploadedimage"
          :draggable="false"
          :src="imageUrl"
          @click="onClick"
        />
      </div>

      <div v-else-if="icon" class="img_empty">
        <div class="icon_contains" @click="onClick">
          <svg-icon :icon="icon" :width="'60'" :height="'60'" />
        </div>
      </div>

      <div v-if="canEdit" class="button-area">
        <VDropdown
          placement="bottom-start"
          popper-class="v-popper--changeCover-dropdown"
        >
          <button type="button" class="icon-change">Change cover</button>
          <template #popper>
            <asset-picker
              :asset-reference="cover"
              :on-select="onSelect"
              :embed-video="false"
            />
          </template>
        </VDropdown>
      </div>
    </div>
    <div v-else class="img_loader">
      <!-- <div class="percentage">100%</div> -->
      <svg-icon icon="app-icons/spinner" :width="'60'" :height="'60'" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import SvgIcon from '../../all/SvgIcon.vue';
import AssetPicker from '../../all/AssetPicker/AssetPicker.vue';
import { convertBytes, has } from '../../../utils.js';
import updater from '../../../vuex/project/updater.js';
import requests from '../../../vuex/requests.js';
import iconMetadata from '../../../vuex/icon-metadata.js';

export default {
  components: {
    AssetPicker,
    SvgIcon,
  },

  props: {
    component: {
      type: Object,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      isAssetOverlayOpen: false,
      isDraggedOver: false,
      dragCounter: 0,
      isLoading: false,
      options: {
        boundary: document.body,
        placement: 'bottom-start',
        triggers: ['click'],
        delay: {
          show: 100,
          hide: 100,
        },
        autoHide: true,
        'popover-inner-class': 'tooltip-inner popover-inner no-padding',
        distance: 3,
      },
    };
  },

  computed: {
    ...mapGetters('application/drag', ['getItems']),
    ...mapGetters('project/data/components', [
      'getNode',
      'getCover',
      'getCoverId',
      'getCoverIcon',
      'getDefaultIcon',
      'getContainedNodes',
    ]),
    ...mapGetters('project/config/components', ['getOpen']),
    ...mapGetters('user/projects', {
      getOpenProject: 'getOpen',
    }),
    ...mapGetters('project/data/assets', ['getMediumImageUrl']),
    ...mapGetters('application/contextMenu', {
      getContextMenuIsEmpty: 'getIsEmpty',
    }),
    ...mapGetters('application/modals', ['getDeleteAssetReferencesModal']),
    ...mapGetters('workspace', ['getFeature']),

    cover() {
      return this.component.assets?.cover;
    },

    /**
     * Return the cover asset id (if exists)
     * @return {String|null}
     */
    coverId() {
      return this.component.assets?.cover?.id;
    },

    /**
     * Return the medium image url (if exists)
     * @return {String|null}
     */
    imageUrl() {
      return this.getMediumImageUrl(this.coverId);
    },

    /**
     * Return the icon if exists
     * @return {String|null}
     */
    icon() {
      if (this.component.assets?.cover?.type === 'icon') {
        return this.component.assets?.cover?.file;
      }
      return this.getDefaultIcon;
    },
  },

  watch: {
    /**
     * Detects if context menu is open or not to disable popup's autoHide
     * @param {*} newValue
     * @param {*} oldValue
     */
    getContextMenuIsEmpty(newValue, oldValue) {
      if (!newValue) {
        this.options.autoHide = false;
      } else if (!this.getDeleteAssetReferencesModal) {
        // Hack to change autoHide after click trigger
        setTimeout(() => {
          this.options.autoHide = true;
        }, 200);
      }
    },

    /**
     * Detects if delete assets modal is open or not to disable popup's autoHide
     * @param {*} newValue
     * @param {*} oldValue
     */
    getDeleteAssetReferencesModal(newValue, oldValue) {
      if (newValue) {
        this.options.autoHide = false;
      } else {
        // Hack to change autoHide after click trigger
        setTimeout(() => {
          this.options.autoHide = true;
        }, 200);
      }
    },
  },

  methods: {
    ...mapActions('application/notifications', ['createError']),
    ...mapActions('project/data/components', ['customUpdate', 'setCover']),
    ...mapActions('project/data/assets', ['createAsset']),
    ...mapActions('application/modals', ['setAssetModal']),

    /**
     * A callback to pass to asset picker (will be executed when the selection changes)
     */
    onSelect(cover) {
      this.setCover({ id: this.component.id, cover, apply: true });
    },

    onClick() {
      let cover = this.getCover(this.component.id);
      if (!cover) {
        cover = { type: 'icon', file: this.getDefaultIcon };
      }
      this.setAssetModal({ value: cover });
    },

    // Something is dropped on the image container
    onDrop(event) {
      if (!this.canEdit) {
        return;
      }
      this.dragCounter = 0;
      this.isDraggedOver = false;

      // Check if it is an asset from the sidebar
      if (this.getItems && this.getItems.type === 'ASSET') {
        this.setCover({
          id: this.component.id,
          cover: { id: this.getItems.ids[0] },
          apply: true,
        });
      }

      // In case it is one or more files
      else if (event.dataTransfer.types.includes('Files')) {
        const file = event.dataTransfer.files[0];
        const sizeInMB = convertBytes(file.size, 'MB');

        const sizeLimit = this.getFeature('upload_asset').limit;

        if (sizeLimit && sizeInMB > sizeLimit) {
          this.createError({
            text: `Image size is ${sizeInMB} MB and your plan has a size limit of: ${sizeLimit} MB`,
          });
          return;
        }
        this.sendImageFile(file);
      } else {
        // Some other data
        const droppedHTML = event.dataTransfer.getData('text/html');
        const dropContext = $('<div>').append(droppedHTML);
        const imageUrl = $(dropContext).find('img').attr('src');
        this.sendImage(imageUrl);
      }
    },

    onDragEnter() {
      this.isDraggedOver = true;
      this.dragCounter += 1;
    },

    onDragLeave() {
      this.dragCounter -= 1;
      if (this.dragCounter === 0) {
        this.isDraggedOver = false;
      }
    },

    /**
     * Upload an image file from the disk. It is triggered when a file is dropped on the image container.
     * @param {File} file
     */
    sendImageFile(file) {
      this.isLoading = true;
      // Get the current image and icon values
      let cover = this.component.cover || null;
      // Temporarily set the icon spinner as component asset
      this.setCover({
        id: this.component.id,
        cover: { type: 'icon', file: 'app-icons/spinner' },
        apply: false,
      });
      updater.complete({ apply: true, undoable: false });
      requests.assets
        .uploadImageFile({
          image: file,
          hash: this.getOpenProject,
          small: { width: 240, height: 240 },
          medium: { width: 500, height: 240 },
        })
        .then(response => {
          const { filename, name } = response.data;
          cover = { type: 'image', file: filename, name };
        })
        .catch(error => {
          const { detail } = error.response.data;
          const { statusText } = error.response;
          if (detail) {
            this.createError({ text: detail });
          } else if (statusText) {
            this.createError({ text: statusText });
          }
        })
        .finally(async () => {
          this.isLoading = false;
          // Set the component's new image
          const assetId = await this.createAsset({
            props: cover,
            apply: false,
          });
          this.setCover({
            id: this.component.id,
            cover: { id: assetId },
            apply: false,
          });
          updater.complete({ apply: true });
        });
    },

    /**
     * Upload an image either from url or from base64 encoded string.
     * @param {String}  imageUrl
     */
    sendImage(imageUrl) {
      this.isLoading = true;
      // Get the current image and icon values
      let cover = this.component.cover || null;
      // Temporarily set the icon spinner as component asset
      this.setCover({
        id: this.component.id,
        cover: { type: 'icon', file: 'app-icons/spinner' },
        apply: false,
      });
      updater.complete({ apply: true, undoable: false });
      requests.assets
        .uploadImage({
          data: imageUrl,
          hash: this.getOpenProject,
          small: { width: 240, height: 240 },
          medium: { width: 500, height: 240 },
        })
        .then(response => {
          const { filename, name } = response.data;
          cover = { type: 'image', file: filename, name };
        })
        .catch(error => {
          const { detail } = error.response.data;
          const { statusText } = error.response;
          if (detail) {
            this.createError({ text: detail });
          } else if (statusText) {
            this.createError({ text: statusText });
          }
        })
        .finally(async () => {
          this.isLoading = false;
          // Set the component's new image
          const assetId = await this.createAsset({
            props: cover,
            apply: false,
          });
          this.setCover({
            id: this.component.id,
            cover: { id: assetId },
            apply: false,
          });
          updater.complete({ apply: true });
        });
    },
  },
};
</script>

<style scoped lang="scss">
.modal #img_container {
  &:hover {
    .button-area .icon-change {
      visibility: visible;
      opacity: 0.6;
    }
  }
  .button-area {
    position: absolute;
    right: 20px;
    top: 20px;
    width: auto;
    .icon-change {
      visibility: hidden;
      opacity: 0;
      transition: all 0.2s;
      position: relative;
      background: rgba(0, 0, 0, 0.8);
      border-radius: 3px;
      color: #d9d9d9;
      border: 1px solid #a9a9a9;
      padding: 5px 8px 5px 8px;
      cursor: pointer;
      &:hover {
        opacity: 1;
      }
      &[data-popper-shown] {
        opacity: 1;
        visibility: visible;
      }
    }
  }
}

.modal #img_container .button-area .v-popover.open .icon-change {
  opacity: 1;
  visibility: visible;
}
.modal #img_container {
  background: #000;
  width: auto;
  min-height: 100px;
  text-align: center;
  margin: 0;
  padding: 0;
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &.dragover:after {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    font-weight: 500;
    color: #db841e;
    content: 'Drop image here';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 1;
    background: rgba(0, 0, 0, 0.6);
    box-shadow: inset 0 0 100px #000;
  }

  &:hover .buttons {
    visibility: visible;
  }
  .buttons {
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    visibility: hidden;
    color: #fff;

    .trashcan {
      // width:30px;
      cursor: pointer;
      opacity: 0.4;
      margin: 1px 0 0 10px;

      &:hover {
        opacity: 0.8;
      }
    }

    .upload {
      // width:30px;
      cursor: pointer;
      opacity: 0.65;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  input[type='file'] {
    display: none;
  }

  .img_ready {
    display: flex;
    align-items: stretch;
  }

  .empty_upload {
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
}

.modal #img_container > div {
  display: flex;
  width: 100%;
}

.modal #img_container .img_empty {
  display: flex;
  align-items: center;
  justify-content: center;
  .icon_contains:hover {
    cursor: pointer;
  }
}

.modal #img_container > div > div {
  display: flex;
  width: 100%;
  align-items: center;
  transition: background 140ms linear;
  position: relative;

  &.hasImage {
    min-height: 240px;
  }
}

.modal #img_container .uploadedimage {
  max-height: 240px;
  max-width: 500px;
  margin: 0 auto;
  padding: 0;
  display: block;
  cursor: pointer;
}

.modal #img_container span {
  font-size: 20px;
  font-weight: 100;
  margin: 0 auto;
  opacity: 0.6;
  cursor: default;
}

.modal #img_container .removeimg:hover {
  opacity: 1;
}

.modal #img_container .img_loader {
  margin: 0 auto;
  padding: 20px;
  position: relative;
  align-items: center;
  display: flex;
  justify-content: center;
  height: 240px;
}

.modal #img_container .img_loader img {
  opacity: 0.8;
}

.modal #img_container .img_loader .percentage {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  align-items: center;
  display: flex;
  justify-content: center;
  opacity: 0.7;
}
</style>

<style lang="scss">
.modal #img_container .img_empty {
  svg path {
    fill: #bcbcbc;
  }
}

.icon_contains:hover {
  svg path {
    fill: #fff !important;
  }
}
</style>
<style lang="scss">
.v-popper__popper.v-popper--theme-dropdown.v-popper--changeCover-dropdown {
  margin-top: 7px;

  .v-popper__inner {
    border: none;
    background: transparent;
    border-radius: 0;
    padding: 0;

    .container {
      border-radius: 3px;
      box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
      color: #bcbcbc;
    }
  }

  .v-popper__arrow-outer {
    border-color: #2c3233;
  }
  .v-popper__arrow-inner {
    border-color: #2c3233;
  }
}
</style>
